import type {
    CreateReportingTaskRequestBody,
    CreateReportingTaskResponse,
    GetReportingInputFileTypeListResponse,
    GetReportingTaskImportResponse,
    GetReportingTaskListRequestQuery,
    GetReportingTaskListResponse,
    GetReportingTaskResponse,
    GetReportingTaskTypeListResponse,
    PrepareReportingInputFileUploadRequestBody,
    PrepareReportingInputFileUploadResponse,
    ReportingTaskStatus,
    RunReportingEtlResponse,
    TriggerReportingTaskImportRequestBody,
    TriggerReportingTaskImportResponse,
} from '@engagement-web-app/common/reporting';
import { APIError } from './types';

export async function fetchReportingTasks({
    from,
    limit,
    search,
}: GetReportingTaskListRequestQuery): Promise<GetReportingTaskListResponse> {
    try {
        const params = new URLSearchParams();
        if (from !== undefined) params.set('from', String(from));
        if (limit !== undefined) params.set('limit', String(limit));
        if (search) params.set('search', search);
        const res = await fetch(`/api/system/reporting/tasks?${params.toString()}`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchReportingTask(taskUuid: string): Promise<GetReportingTaskResponse> {
    try {
        const res = await fetch(`/api/system/reporting/tasks/${taskUuid}`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function createReportingTask(body: CreateReportingTaskRequestBody): Promise<CreateReportingTaskResponse> {
    try {
        const res = await fetch('/api/system/reporting/tasks', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function runReportingEtl(taskUuid: string): Promise<RunReportingEtlResponse> {
    try {
        const res = await fetch(`/api/system/reporting/tasks/${taskUuid}/etl`, {
            method: 'POST',
        });
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function triggerReportingTaskImport(
    taskUuid: string,
    body: TriggerReportingTaskImportRequestBody
): Promise<TriggerReportingTaskImportResponse> {
    try {
        const res = await fetch(`/api/system/reporting/tasks/${taskUuid}/import`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchReportingTaskImport(taskUuid: string): Promise<GetReportingTaskImportResponse> {
    try {
        const res = await fetch(`/api/system/reporting/tasks/${taskUuid}/import`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchReportingTaskStatuses(taskUuid: string): Promise<ReportingTaskStatus[]> {
    try {
        const res = await fetch(`/api/system/reporting/tasks/${taskUuid}/statuses`);
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function prepareReportingInputFileUpload(
    body: PrepareReportingInputFileUploadRequestBody
): Promise<PrepareReportingInputFileUploadResponse> {
    try {
        const res = await fetch('/api/system/reporting/input-files/upload-urls', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchReportingInputFileTypes(): Promise<GetReportingInputFileTypeListResponse> {
    try {
        const res = await fetch('/api/system/reporting/input-file-types');
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}

export async function fetchReportingTaskTypes(): Promise<GetReportingTaskTypeListResponse> {
    try {
        const res = await fetch('/api/system/reporting/task-types');
        const data = await res.json();
        if (!res.ok) throw APIError.fromResponse(data);
        return data;
    } catch (error) {
        if (error instanceof APIError) throw error;
        throw APIError.default();
    }
}
